import { useEffect, useId, useRef, useState, type CSSProperties } from 'react';
import { fetchBrandConfig } from 'AppBranding';
import ClientStorage from 'ClientStorage';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import FrontRoyalSpinner from 'FrontRoyalSpinner';
import parseHtml from 'html-react-parser';
import { type AnyObject } from '@Types';
import { twMerge } from 'tailwind-merge';
import { ProgramTypeConfigs } from 'Program';
import { downloadOrShareSVG } from '../../helpers/helpers';
import { downloadSupported, shareSupported } from '../../helpers/downloadHelpers';
import { fontConfigs, SVG_VIEWBOX_HEIGHT, SVG_VIEWBOX_WIDTH } from '../../constants';
import { programAchievementGraphicDesigns } from '../../programAchievementGraphicDesigns';
import {
    type ProgramAchievementGraphicDesignKey,
    type ProgramAchievementGraphicKey,
    type ProgramAchievementGraphicUser,
} from '../../ShareableGraphics.types';

// oxlint-disable-next-line max-lines-per-function, complexity
export function ProgramAchievementGraphic({
    variant,
    collapsed,
    currentUser,
    xsOrSm,
    thumbnailOnly = false,
    className,
    version = 'v2',
}: {
    variant: ProgramAchievementGraphicKey;
    collapsed: boolean;
    currentUser: ProgramAchievementGraphicUser;
    xsOrSm: boolean;
    thumbnailOnly?: boolean;
    className?: string;
    version?: 'v1' | 'v2';
}) {
    const isV1 = version === 'v1';
    const { t } = useTranslation('back_royal');
    const idPrefix = useId();

    const brandConfig = currentUser?.cohort ? fetchBrandConfig(currentUser.cohort.branding) : null;
    const graphicsConfig = brandConfig?.programAchievementGraphics;
    const programTitleLines = currentUser?.cohort
        ? ProgramTypeConfigs[currentUser.cohort.programType].programAchievementGraphicProgramTitleLines(
              currentUser.cohort,
          )
        : [];

    const containerRef = useRef<HTMLDivElement>(null);

    const clientStorageDesignKey = `program_achievement_graphic_${variant}_design`;
    const storedDesignKey = ClientStorage.getItem(clientStorageDesignKey);
    const graphicConfig = graphicsConfig?.[variant];

    const availableDesignKeys = graphicConfig?.availableDesigns;
    const defaultSelected = availableDesignKeys?.[0];
    const initialSelected = availableDesignKeys?.includes?.(storedDesignKey as ProgramAchievementGraphicDesignKey)
        ? (storedDesignKey as ProgramAchievementGraphicDesignKey)
        : defaultSelected;

    const [selectedDesignKey, setSelectedDesignKey] = useState<ProgramAchievementGraphicDesignKey>(
        initialSelected as ProgramAchievementGraphicDesignKey,
    );
    const [hoveredDesignKeys, setHoveredDesignKeys] = useState<AnyObject<boolean>>();
    const [shareIsSupported, setShareIsSupported] = useState(false);
    const [downloading, setDownloading] = useState(false);

    const showDownload = downloadSupported() || shareIsSupported;
    const selectedDesign = programAchievementGraphicDesigns[selectedDesignKey];

    useEffect(() => {
        ClientStorage.setItem(clientStorageDesignKey, selectedDesignKey);
    }, [clientStorageDesignKey, selectedDesignKey]);

    useEffect(() => {
        if (defaultSelected && !availableDesignKeys?.includes?.(selectedDesignKey)) {
            setSelectedDesignKey(defaultSelected!);
        }
    }, [availableDesignKeys, selectedDesignKey, setSelectedDesignKey, defaultSelected]);

    useEffect(() => {
        shareSupported().then(setShareIsSupported);
    }, [setShareIsSupported]);

    const getDesignBackgroundStyle = (designKey: ProgramAchievementGraphicDesignKey): CSSProperties => {
        const design = programAchievementGraphicDesigns[designKey];
        const background = `linear-gradient(-20deg, ${design.bgGradient.startColor.hex}, ${design.bgGradient.endColor.hex})`;
        return { background };
    };

    // We only apply a background style for the surrounding background at xs/sm screen sizes.
    const getSurroundingBackgroundStyle = () => (xsOrSm ? getDesignBackgroundStyle(selectedDesignKey) : undefined);

    const getDesignBubbleStyles = (designKey: ProgramAchievementGraphicDesignKey) => {
        const design = programAchievementGraphicDesigns[designKey];
        const styles = getDesignBackgroundStyle(designKey);
        const isSelected = selectedDesignKey === designKey;
        const isHovered = !!hoveredDesignKeys?.[designKey];
        if (isSelected || isHovered) {
            // When the design bubble is selected, we add a ring around the design bubble.
            // Similarly, if the mouse is hovering over a design bubble, then we also add
            // the ring around the design bubble, but fade it a bit. If the mouse is hovering
            // over the design bubble belonging to the selectedDesign, then we don't bother
            // fading the ring.
            const { r, g, b } = design.ringRGBA;
            styles.boxShadow = `0 0 0 4px rgba(${r}, ${g}, ${b}, ${isSelected ? 1 : 0.6})`;
        } else {
            styles.boxShadow = 'none';
        }
        return styles;
    };

    const handleDesignClick = (designKey: ProgramAchievementGraphicDesignKey) => {
        setSelectedDesignKey(designKey);
    };

    const handleDesignHover = (designKey: ProgramAchievementGraphicDesignKey, hovering: boolean) => {
        setHoveredDesignKeys(val => ({
            ...val,
            [designKey]: hovering,
        }));
    };

    const handleDownloadClick = () => {
        if (!showDownload || downloading) return;

        downloadOrShareSVG({
            setDownloading,
            containerElem: containerRef.current!,
            fileName: `${currentUser.name}-${variant}-image-${selectedDesignKey}`,
            width: SVG_VIEWBOX_WIDTH * 2,
            height: SVG_VIEWBOX_HEIGHT * 2,
            fontConfigs,
        });
    };

    if (!selectedDesign || !graphicConfig || !brandConfig) return null;

    if (thumbnailOnly) {
        return (
            <div
                className={clsx(
                    'program-achievement-graphic box-contents h-full max-h-full w-full object-contain p-0',
                    variant,
                    selectedDesignKey,
                )}
                data-testid="program-achievement-graphic"
            >
                <graphicConfig.component
                    selectedDesign={selectedDesign}
                    brandConfig={brandConfig}
                    currentUser={currentUser}
                    idPrefix={idPrefix}
                    programTitleLines={programTitleLines}
                />
            </div>
        );
    }

    return (
        <div
            ref={containerRef}
            key={variant}
            className={twMerge(
                isV1 && 'program-achievement-graphic box-contents max-md:min-h-full max-md:w-full max-md:py-8',
                !isV1 && '',
                variant,
                selectedDesignKey,
                className,
            )}
            data-testid="program-achievement-graphic"
            style={getSurroundingBackgroundStyle()}
        >
            {collapsed && <div className="collapsed-container invisible h-full" />}
            {!collapsed && (
                <div
                    className={clsx(
                        isV1 &&
                            'version-1 expanded-container max-md:mx-auto max-md:w-11/12 max-md:max-w-[350px] max-md:shadow-[0px_8px_11px_rgba(0,0,0,0.5)]',
                        !isV1 && '',
                    )}
                >
                    <div className="main-box">
                        <graphicConfig.component
                            selectedDesign={selectedDesign}
                            brandConfig={brandConfig}
                            currentUser={currentUser}
                            idPrefix={idPrefix}
                            programTitleLines={programTitleLines}
                        />
                    </div>
                    <div
                        className={clsx(
                            isV1 &&
                                'download-design-container flex items-start justify-between bg-white pt-[18px] sm:max-md:rounded',
                            !isV1 && 'flex items-start justify-between rounded-2 bg-white pt-[18px]',
                        )}
                    >
                        <div
                            data-testid="design-bubbles-container"
                            className={clsx(
                                'my-0 text-center text-2xs text-map-blue-dark rtl:ml-auto rtl:mr-5',
                                showDownload ? 'ml-5 mr-auto' : 'mx-auto',
                            )}
                        >
                            <div className="design-bubbles mx-auto my-0 flex w-[110px] justify-between pb-[10px]">
                                {availableDesignKeys!.map((designKey: ProgramAchievementGraphicDesignKey) => (
                                    <button
                                        key={designKey}
                                        type="button"
                                        aria-label={`Select ${designKey} design`}
                                        className={clsx(
                                            'design-bubble m-0 h-[30px] w-[30px] cursor-pointer rounded-full border-2 border-solid border-white p-0 transition-none focus:outline-0',
                                            designKey,
                                        )}
                                        style={getDesignBubbleStyles(designKey)}
                                        onClick={() => handleDesignClick(designKey)}
                                        onMouseEnter={() => handleDesignHover(designKey, true)}
                                        onMouseLeave={() => handleDesignHover(designKey, false)}
                                    />
                                ))}
                            </div>
                            <div className="instructions">
                                {t('program_achievement_graphic.program_achievement_graphic.select_a_design')}
                            </div>
                        </div>
                        {showDownload && (
                            <div
                                data-testid="download-container-wrapper"
                                className="download-container-wrapper mb-0 ml-auto mr-4 mt-0 pt-[1px] text-[13px] font-semibold text-blue rtl:ml-4 rtl:mr-auto"
                            >
                                {downloading && (
                                    <div className="downloading-container">
                                        <span>
                                            {t('program_achievement_graphic.program_achievement_graphic.downloading')}
                                        </span>
                                        <FrontRoyalSpinner className="blue mx-auto my-[5px]" />
                                    </div>
                                )}
                                {!downloading && (
                                    <button
                                        className="download-container group m-0 flex cursor-pointer justify-end border-0 bg-transparent p-0 text-center text-inherit shadow-none"
                                        onClick={() => handleDownloadClick()}
                                        type="button"
                                    >
                                        <span className="instructions mr-[7px] text-[13px] uppercase leading-4 text-blue group-hover:underline rtl:ml-[7px] rtl:mr-0">
                                            {parseHtml(
                                                t(
                                                    'program_achievement_graphic.program_achievement_graphic.download_and_share',
                                                ),
                                            )}
                                        </span>
                                        <i className="fas fa-arrow-alt-circle-down text-[30px]" />
                                    </button>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}
